/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component } from "react";
import Tooltip from "@react/react-spectrum/Tooltip";
import * as constants from "constants/constants";
import ProgressBarCss from "./ProgressBar.css";
import { isCurrentDeviceMobile, isCurrentDeviceTablet } from "dc-preview-utils/src/Utils/DeviceUtils";
import CommonCss from "../common/common.css";
import { animationType } from "constants/enums";
import ScrubberUICss from "components/scrubber/ScrubberUI.css";
import { openPopUP } from "components/common/scrubber/pagePopUp";
import { ScrubberMidDiv } from "components/common/scrubber/ScrubberMidDiv";
import classNames from "classnames";

class ProgressBar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pageNumber: props.pageIndex + 1,
      popOverLeft: 0,
      showPopOver: false,
      showBlueDial: false,
      updateOnPageIndex: true,
      xPosition: 0,
      draggableLoaded: false,
      isDraggingInProgress: false,
      isDragged: false,
      showScrubberMidDiv: false
    };
    this.handleClick = this.handleClick.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragStop = this.handleDragStop.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.isCurrentDeviceMobileOrTablet = isCurrentDeviceTablet() || isCurrentDeviceMobile();
    /* In IE and Edge, mouse move keeps firing after mouse leave because of which scrubber does not disappear. Hence, setting a flag after mouse leave to fix this issue */
    this.isMouseWithinProgressBar = false;
    this.handleTouchMoveOnDraggable = this.handleTouchMoveOnDraggable.bind(this);
    this.setProgressBarInteractive = this.setProgressBarInteractive.bind(this);
    this.getPageNumberFromEventData = this.getPageNumberFromEventData.bind(this);
    this.setPageNumber = this.setPageNumber.bind(this);
  }

  loadReactDraggable() {
    import("react-draggable")
      .then(draggable => {
        this.reactDraggable = draggable.DraggableCore;
        this.setState({
          draggableLoaded: true
        });
      })
      .catch(err => {
        console.error(err);
      });
  }

  getPageNumberFromEventData(event) {
    return Math.ceil((event.clientX - this.props.getDocumentViewLeft) / this.props.pageWidth);
  }

  limitPageNum(num) {
    if (num === 0) return 1;
    else if (num > this.props.numOfPages) return this.props.numOfPages;
    return num;
  }

  getPageNumber() {
    if (this.props.scrubberMode) {
      return Math.floor((this.props.pageWidth + this.state.xPosition) / this.props.pageWidth);
    }
    return Math.ceil((this.props.pageWidth + this.state.xPosition) / this.props.pageWidth);
  }

  getPopOverPosition(x) {
    let calculatedPopoverLeft =
      x - this.props.left - (this.popOver ? this.popOver.offsetWidth / 2 : 0) - this.props.getDocumentViewLeft;
    if (calculatedPopoverLeft < 0) calculatedPopoverLeft = 0;
    if (
      calculatedPopoverLeft + this.popOver.offsetWidth + constants.PROGRESS_BAR_TOOLTIP_RIGHT_MARGIN >=
      this.props.progressBarWidth
    ) {
      calculatedPopoverLeft =
        this.props.progressBarWidth - this.popOver.offsetWidth - constants.PROGRESS_BAR_TOOLTIP_RIGHT_MARGIN;
    }
    return calculatedPopoverLeft;
  }

  handleDragStart() {
    this.setState({
      updateOnPageIndex: false,
      xPosition: this.props.pageIndex * this.props.pageWidth,
      isDraggingInProgress: true
    });
  }

  handleDrag(ev, drag) {
    let x = this.state.xPosition;
    let xPosition = x + drag.deltaX > this.props.progressBarWidth ? this.props.progressBarWidth : x + drag.deltaX;
    let leftLimit = 0;
    if (!this.props.scrubberMode) {
      leftLimit = -(this.props.pageWidth - constants.PROGRESS_BAR_SCRUBBER_HEIGHT);
    }
    if (xPosition < leftLimit) xPosition = leftLimit;

    const pageNumber = this.limitPageNum(this.getPageNumber());
    const popOverLeft = this.popOver ? this.getPopOverPosition(ev.clientX) : this.state.popOverLeft;

    this.setState({
      xPosition,
      pageNumber,
      popOverLeft,
      showScrubberMidDiv: true,
      isDragged: true
    });

    this.props.onClick(pageNumber);
  }

  handleDragStop(ev) {
    if (this.props.scrubberMode && !this.state.isDragged) openPopUP(this.props.numOfPages);
    this.setState({
      updateOnPageIndex: true,
      showPopOver: false,
      isDraggingInProgress: false,
      showScrubberMidDiv: false,
      isDragged: false
    });
  }

  handleTouchMoveOnDraggable(ev) {
    /* Bug fix ADCS: 9123 In case of iOS devices, touch event propagating to document on dragging react-draggable scrubber,
      causing document to drag along with blue dial and causing inconsistent page number state of document
    */
    if (this.state.isDraggingInProgress) {
      ev.preventDefault();
    }
  }

  handleClick(ev) {
    if (!this.props.scrubberMode && !this.props.isHudAnimationOut) {
      this.setState({
        showPopOver: false,
        showBlueDial: true
      });
      const pageNumber = this.limitPageNum(this.getPageNumberFromEventData(ev));
      this.props.onClick(pageNumber);
    }
  }

  handleMouseLeave() {
    if (!this.props.scrubberMode && !this.state.isDraggingInProgress) {
      this.isMouseWithinProgressBar = false;
      this.setState({
        showPopOver: false,
        showBlueDial: false
      });
    }
  }

  handleMouseEnter(ev) {
    if (
      !this.props.scrubberMode &&
      !this.isCurrentDeviceMobileOrTablet &&
      !this.state.isDraggingInProgress &&
      !this.props.isHudAnimationOut
    ) {
      this.isMouseWithinProgressBar = true;
      this.setState({
        showPopOver: true,
        showBlueDial: true
      });
    }
  }

  handleMouseMove(ev) {
    if (
      !this.props.scrubberMode &&
      !this.isCurrentDeviceMobileOrTablet &&
      !this.state.isDraggingInProgress &&
      this.isMouseWithinProgressBar &&
      !this.props.isHudAnimationOut
    ) {
      const popOverLeft = this.popOver ? this.getPopOverPosition(ev.clientX) : this.state.popOverLeft;
      const pageNumber = this.limitPageNum(this.getPageNumberFromEventData(ev));
      this.setState({
        showPopOver: true,
        showBlueDial: true,
        pageNumber,
        popOverLeft
      });
    }
  }

  setProgressBarInteractive(isInteractive) {
    this.setState({
      showPopOver: !isInteractive,
      showBlueDial: !isInteractive
    });
  }

  setPageNumber(pageNumber) {
    this.setState({
      pageNumber
    });
  }

  componentDidMount() {
    this.loadReactDraggable();
    this.props.disableContextMenu && this.props.disableContextMenu(ProgressBarCss.ProgressBarContainer);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevProps.isHudAnimationOut && this.props.isHudAnimationOut) {
      this.setProgressBarInteractive(false);
    }
    if (prevProps.isHudAnimationOut && !this.props.isHudAnimationOut) {
      this.setProgressBarInteractive(true);
    }
    if (prevProps.pageIndex !== this.props.pageIndex) {
      this.setPageNumber(this.props.pageIndex + 1);
    }
  }

  render() {
    document.ontouchmove = this.state.isDraggingInProgress ? this.handleTouchMoveOnDraggable : null;
    let style = {
      position: "absolute",
      bottom: this.props.bottom,
      backgroundColor: this.props.scrubberMode ? "transparent" : constants.PROGRESS_BAR_BACKGROUND_COLOR,
      left: this.props.left,
      width: this.props.progressBarWidth,
      height: constants.PROGRESS_BAR_HEIGHT,
      cursor: !(this.props.isHudAnimationOut || this.props.scrubberMode) ? "pointer" : "auto",
      zIndex: "15000",
      opacity: this.props.scrubberMode ? 1 : constants.HUD_PROGRESS_BAR_OPACITY
    };
    const pageNumber = this.state.pageNumber;
    const left = this.state.popOverLeft;

    let popOverStyle = {
      position: "absolute",
      bottom: this.props.popOverBottom,
      left
    };

    const width = this.props.numOfPages !== 0 ? this.props.pageWidth : 0;
    let innerDivWidth = this.state.updateOnPageIndex
      ? (this.props.pageIndex + 1) * width
      : width + this.state.xPosition;

    if (this.props.scrubberMode) innerDivWidth -= width;
    if (innerDivWidth > this.props.progressBarWidth) {
      innerDivWidth = this.props.progressBarWidth;
    }

    const innerDivStyle = {
      position: "absolute",
      height: constants.PROGRESS_BAR_HEIGHT,
      backgroundColor: constants.PROGRESS_BAR_ACTIVE_COLOR,
      width: innerDivWidth,
      cursor: !this.props.isHudAnimationOut ? "pointer" : "auto",
      borderRadius: "0px 10px 10px 0px"
    };

    let leftVal = innerDivWidth;
    if (!this.props.scrubberMode) {
      leftVal -= constants.PROGRESS_BAR_SCRUBBER_HEIGHT;
    }
    const draggableContainerLeft = leftVal > 0 ? leftVal : 0;

    const blueDialStyle = {
      position: "absolute",
      backgroundColor: constants.PROGRESS_BAR_ACTIVE_COLOR,
      border: "2px solid var(--spectrum-global-color-gray-50)",
      borderRadius: "10px",
      top: "16px",
      height: "14px",
      width: "14px",
      zIndex: "15000"
    };

    const draggableContainerStyle = {
      position: "absolute",
      top: this.props.scrubberMode ? "-27px" : "-23px",
      height: "32px",
      width: "32px",
      left: `${draggableContainerLeft}px`,
      zIndex: "15000"
    };

    const extendedAreaStyle = {
      height: `${constants.PROGRESS_BAR_SCRUBBER_HEIGHT}px`,
      position: "absolute",
      bottom: "-6px",
      width: "100%"
    };

    let wrapperStyle = {};
    let animationInStyle, animationOutStyle;
    if (this.props.applyAnimationClasses) {
      if (this.props.animationType === animationType.SLIDE) {
        animationOutStyle = CommonCss.slideOut;
        animationInStyle = CommonCss.slideIn;
      } else if (this.props.animationType === animationType.FADE) {
        animationOutStyle = CommonCss.fadeOut;
        animationInStyle = CommonCss.fadeIn;
      }
      if (this.props.isHudAutoHiding) {
        if (!this.props.isHudAnimationOut) {
          wrapperStyle.animation = `${animationOutStyle} ${this.props.animationOutDuration / 1000}s`;
        }
      } else {
        wrapperStyle.animation = `${animationInStyle} ${this.props.animationInDuration / 1000}s`;
      }
    }
    let className = ScrubberUICss.ScrubberUI;
    className = classNames(className, ScrubberUICss.ScrubberUIHorizontal);

    return (
      <div className={ProgressBarCss.ProgressBarContainer} style={wrapperStyle}>
        {this.state.showPopOver ? (
          <div
            ref={popOver => {
              this.popOver = popOver;
            }}
            style={popOverStyle}
          >
            <Tooltip placement="top" className={ProgressBarCss.tooltip}>
              {this.props.intl.formatMessage(
                { id: "modernviewer.ariaLabel.thumbnails.pages" },
                { pageNum: pageNumber }
              )}
            </Tooltip>
          </div>
        ) : null}
        {this.props.scrubberMode && this.props.shouldShowScrubber ? (
          <ScrubberMidDiv
            pageNumber={this.state.pageNumber}
            show={this.state.showScrubberMidDiv}
            intl={this.props.intl}
          />
        ) : null}
        <div style={style} onMouseMove={this.handleMouseMove}>
          {!this.props.scrubberMode ? <div style={innerDivStyle} className={ProgressBarCss.progressBarHC} /> : null}
          <div
            style={extendedAreaStyle}
            ref={progressBar => {
              this.progressBar = progressBar;
            }}
            onMouseEnter={this.handleMouseEnter}
            onMouseLeave={this.handleMouseLeave}
            onClick={this.handleClick}
          />
          {this.state.draggableLoaded && (this.props.scrubberMode || this.state.showBlueDial) ? (
            <this.reactDraggable
              axis="none"
              onStart={this.handleDragStart}
              onDrag={this.handleDrag}
              onStop={this.handleDragStop}
            >
              <div
                style={draggableContainerStyle}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
                onClick={this.handleClick}
              >
                {this.props.scrubberMode ? (
                  this.props.shouldShowScrubber ? (
                    <div className={className}>
                      <div className={ScrubberUICss.ScrubberPageNumHorizontal}>{this.state.pageNumber}</div>
                    </div>
                  ) : null
                ) : (
                  <div style={blueDialStyle} />
                )}
              </div>
            </this.reactDraggable>
          ) : null}
        </div>
      </div>
    );
  }
}

export { ProgressBar };
